// Imagem com fallback automático: se quebrar/404, mostra placeholder com iniciais.
// Usado em pôsters de filme/série, capas de episódio, logos de canal.
//
// Fallback bonito (default): degradê derivado de hash do texto + iniciais
// grandes + textura diagonal sutil. Caller pode override via
// placeholderClassName quando precisar de tamanho/estilo específico
// (ex: chip pequeno).

import { getCapability } from '@app-iptv/device'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getApiBase } from '../api'
import { readPosterSize, readRecPosterSize, readThumbSize } from '../lib/card-metrics'
import { buildDirectImgUrl, imagesUseProxy } from '../lib/image-source'
import { isEntryVisible } from '../lib/intersection'

import containerStyles from './Poster.module.css'

interface PosterProps {
  src: string | null | undefined
  alt: string
  fallbackText: string
  /** Imagem alternativa quando `src` falha (404, broken URL).
   *  Caso típico: thumb de episódio Xtream quebrada → cair pro backdrop
   *  da série. Se `fallbackSrc` também falhar, aí vira placeholder de
   *  iniciais. Quando ambos null, vai direto pra placeholder. */
  fallbackSrc?: string | null | undefined
  /** Tipo de uso da imagem — controla o tamanho TMDB pedido ao proxy.
   *  Default `'poster'` (w300) que cobre cards verticais comuns.
   *  Use `'poster-large'` em hero secundário, `'thumb'` em 16:9,
   *  `'backdrop'` em tela cheia, `'logo'` pra passthrough. */
  kind?: ImageKind | undefined
  /** Kind separado pra `fallbackSrc`. Útil quando o fallback tem
   *  proporções/uso diferente do primary. Caso real: thumb 16:9 falha
   *  (`kind='thumb'`, w300), cai pro backdrop da série — backdrop usado
   *  em thumb 16:9 fica OK em w780/w1280, NÃO em w300 (pixelizado).
   *  Default: mesmo `kind` do primary. */
  fallbackKind?: ImageKind | undefined
  className?: string | undefined
  placeholderClassName?: string | undefined
  loading?: 'lazy' | 'eager' | undefined
  /** Hint de prioridade pro browser. `'high'` em imagens above-the-fold
   *  (hero, primeiros cards do catálogo) acelera carregamento — browser
   *  prioriza vs imagens off-screen / lazy. */
  fetchPriority?: 'high' | 'low' | 'auto' | undefined
}

/**
 * Resolve a URL final da imagem. DUAS SAÍDAS, escolhidas por CAPABILITY
 * (`lib/image-source.ts`), nunca por marca de fabricante:
 *
 *  - **TV (app empacotado, `file://`)**: DIRETO. TMDB com a variante de tamanho
 *    montada na própria URL; capa/logo do painel Xtream com a URL ORIGINAL,
 *    intacta. Zero tráfego pela nossa infra — a mesma premissa que já vale pro
 *    vídeo. Em `file://` não há contexto seguro, então `http://` do painel
 *    carrega sem bloqueio de mixed content.
 *  - **WEB (navegador, http/https)**: PROXY (dev: `/img-proxy` do plugin Vite;
 *    prod: `${getApiBase()}/v1/proxy/img`). Lá o proxy resolve mixed content de
 *    verdade (página https não carrega imagem http), alguns CDNs Xtream só
 *    servem HTTP e redirecionam HTTPS pra HTML, e o SW cacheia por ETag.
 *
 * Exportada pra uso fora do <Poster> (ex: backdrop via background-image no CSS).
 *
 * `kind` controla o tamanho TMDB pedido — no web vira `?w=` pro backend, na TV
 * vira o segmento `/t/p/<size>/` da URL direta. Idempotente em URLs não-TMDB
 * (logos Xtream passam intactos) nos dois caminhos:
 *   - poster:        DERIVADO de `--card-vod-w` × DPR útil × teto do tier
 *   - poster-large:  w500  (hero secundário, MovieDetails poster)
 *   - thumb:         DERIVADO de `--card-episode-w` (escada de still, teto w300)
 *   - backdrop:      depende de viewport (w780 / w1280)
 *   - logo:          passthrough (logos Xtream não têm variants)
 */

/** Bucket do cache do SW. Vem de `getCapability('swImageCacheSuffix')` —
 *  storage-aware quando navigator.storage disponível ('storage-vlow'),
 *  fallback CPU+RAM tier ('mid') senão. SW.ts aceita ambos formatos.
 *  Cacheado pelo módulo capabilities — leitura é cheap.
 *  Nome `getCacheBucket` em vez de `getDeviceTier` porque o retorno pode
 *  ser 'storage-vlow' (não tier) — confunde caller. */
function getCacheBucket(): string {
  return getCapability('swImageCacheSuffix')
}

export type ImageKind =
  | 'poster'
  | 'poster-small'
  | 'poster-large'
  | 'thumb'
  | 'backdrop'
  | 'logo'

interface RewriteOpts {
  kind?: ImageKind
}

/** Decide o size TMDB de backdrop conforme viewport real do device.
 *  TV 720p/1080p → w780 já cobre. 4K e além → w1280 (capado deliberadamente).
 *  `original` foi descartado porque pode pesar 3-5MB; em catálogo com 50
 *  backdrops carregados em cascata, viraria 250MB de transferência. w1280
 *  é nítido em TV 4K à distância de visualização normal de TV.
 *  Cacheado module-level (viewport não muda em TV durante a sessão). */
let _cachedBackdropSize: string | null = null
function backdropSizeForViewport(): string {
  if (_cachedBackdropSize !== null) return _cachedBackdropSize
  if (typeof screen === 'undefined') {
    _cachedBackdropSize = 'w1280'
    return _cachedBackdropSize
  }
  // Multiplica por DPR pra cobrir TVs 4K que reportam screen.width=1920
  // mas têm DPR=2 (alguns Tizen). Fallback DPR=1.
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  const effectiveWidth = screen.width * dpr
  _cachedBackdropSize = effectiveWidth <= 1280 ? 'w780' : 'w1280'
  return _cachedBackdropSize
}

/** Mapeia kind → size TMDB. `null` significa "passthrough" (sem ?w=).
 *  Só afeta URLs `image.tmdb.org` — capa de provider Xtream NÃO tem variante
 *  de tamanho e passa intacta pelo proxy (nada a otimizar lá pelo `?w=`).
 *
 *  PROIBIDO cravar largura de card aqui. Este arquivo TINHA
 *  `POSTER_CSS_WIDTH = 180` ("não é estimativa", jurava o comentário) e um
 *  short-circuit que forçava `w154` em vlow. O card virou 256px na branch de
 *  densidade e os dois números viraram ficção: o pôster ficou BORRADO na TV
 *  (upscale de 1.66x) e o tamanho pedido descolou do tamanho rasterizado. Agora
 *  a decisão sai de `lib/card-metrics` — largura COMPUTADA do card × DPR útil ×
 *  teto por tier. Ver o cabeçalho de card-metrics.ts. */
function sizeForKind(kind: ImageKind): string | null {
  switch (kind) {
    case 'poster':
      return readPosterSize()
    case 'poster-small':
      // Trilho de recomendações: card próprio (--card-rec-w), menor que o de VOD.
      return readRecPosterSize()
    case 'poster-large':
      return 'w500'
    case 'thumb':
      return readThumbSize()
    case 'backdrop':
      return backdropSizeForViewport()
    case 'logo':
    default:
      return null
  }
}

/**
 * `loading="lazy"` NÃO EXISTE no Chrome 53 (webOS) nem no 38: o atributo é
 * ignorado EM SILÊNCIO — sem erro, sem aviso. Foi por isso que o profile na TV
 * real achou 615 <img> montadas com só 7 no viewport e as outras 608 já
 * BAIXADAS E DECODADAS. `decoding="async"` idem.
 *
 * `IntersectionObserver`, por outro lado, É suportado no Chr53 — então dá pra
 * fazer o lazy de verdade com ele. Feature-detect por CAPABILITY (`'loading' in
 * HTMLImageElement.prototype`), nunca `if (webOS)`: onde o nativo existe (web,
 * TV moderna) o browser resolve e o polyfill nem liga.
 */
const HAS_NATIVE_LAZY =
  typeof HTMLImageElement !== 'undefined' && 'loading' in HTMLImageElement.prototype

/** Margem de pré-carga do polyfill. Carrega a imagem um pouco ANTES de ela
 *  entrar na tela — em TV o foco anda rápido e imagem chegando tarde é
 *  percebida como "card vazio". */
const LAZY_ROOT_MARGIN = '200px'

export function rewriteImgUrl(src: string | null | undefined, opts: RewriteOpts = {}): string {
  // Defesa: chamadas externas podem passar null/undefined/'' — não-fatal.
  if (!src) return ''
  // SSR-safe: sem window, retorna URL crua (build-time render)
  if (typeof window === 'undefined') return src
  // URLs locais ou data: — sem proxy
  if (
    src.startsWith('/') ||
    src.startsWith('data:') ||
    src.startsWith('blob:') ||
    src.startsWith(window.location.origin)
  ) {
    return src
  }
  const size = sizeForKind(opts.kind ?? 'poster')

  // ===== TV: A IMAGEM VAI DIRETO. O PROXY É WEB-ONLY. =====
  // Regra de produto (dura): não roteamos tráfego de TV pela nossa infra —
  // milhares de TVs × centenas de capas cada seria banda/custo nosso pra servir
  // um byte que a origem já serve. Vale o mesmo princípio do VÍDEO (stream-url.ts
  // devolve 'direct' na TV). A decisão é por CAPABILITY (app empacotado em
  // `file://` / superfície de TV), nunca `if (webOS)` — ver lib/image-source.ts.
  //
  // O que sai daqui na TV:
  //   - TMDB  → `https://image.tmdb.org/t/p/<size>/<path>` (variante pronta na
  //     própria URL, mesmo `size` que o backend usaria — função pura compartilhada).
  //   - Xtream → a URL ORIGINAL, intacta (inclusive `http://`, que em `file://`
  //     carrega: não há mixed content fora de contexto seguro).
  // Este early-return é o que o guard de classe (image-source.test.ts + o bloco
  // "TV" de Poster.dom.test.tsx) trava: se a TV voltar a montar `/v1/proxy/img`
  // pra imagem, os testes ficam vermelhos.
  if (!imagesUseProxy()) return buildDirectImgUrl(src, size)

  const tier = getCacheBucket()

  const buildUrl = (basePath: string): string => {
    const params = new URLSearchParams()
    params.set('tier', tier)
    if (size) params.set('w', size)
    params.set('u', src)
    return `${basePath}?${params.toString()}`
  }

  // Em dev, usa /img-proxy do Vite plugin (origin http → relativo resolve).
  if (import.meta.env.DEV) {
    return buildUrl('/img-proxy')
  }

  // ===== A BASE DO BACKEND É RUNTIME, NUNCA build-time =====
  // Isto lia `import.meta.env.VITE_API_BASE_URL` direto. Esse env NÃO é injetado
  // no pacote de TV (só existe no .env.example, apontando pra localhost): a base
  // real vem do runtime-config carregado no boot — é o que permite failover de
  // endpoint sem rebuild (ver getApiBase()/api.ts). Resultado do bug: `apiBase`
  // vinha '' e a URL saía ROOT-RELATIVA (`/v1/proxy/img?...`). No browser isso até
  // resolve contra o origin http; na TV o app roda em `file://` e a URL vira
  // `file:///v1/proxy/img?...` → net::ERR_ACCESS_DENIED em TODA imagem. Foi o
  // "está tudo sem cover" na LG: nem lazy-load nem tamanho — o pôster nunca teve
  // uma URL alcançável. Medido no aparelho (webOS/Chr53, 2.11.1): 20/20 imagens
  // ERR_ACCESS_DENIED contra `file:///v1/proxy/img`, enquanto as chamadas de dados
  // iam pra `https://api.swipetvplayer.com` (200). getApiBase() é a MESMA fonte
  // que a camada de dados e a de sync usam — nunca reintroduzir o env aqui.
  const apiBase = getApiBase()

  // Rede de segurança: sem base resolvida (runtime-config ainda não plugado, ou
  // fetch de config falhou), emitir uma URL root-relativa é emitir uma URL
  // GARANTIDAMENTE morta em `file://`. Cair no caminho DIRETO faz a imagem
  // carregar da origem (TMDB é https e responde, no tamanho certo): perde-se o
  // cache do SW, NÃO a imagem. Degradação = "imagem sem cache nosso", jamais
  // "card sem capa".
  if (!apiBase) return buildDirectImgUrl(src, size)

  return buildUrl(`${apiBase}/v1/proxy/img`)
}

/**
 * Superfície NEUTRA do placeholder — usada quando não há nada pra rotular.
 *
 * Mesmo par de cores que o `.placeholder` do hero da Live já usa
 * (`linear-gradient(135deg, #1a1a1a, var(--bg-deepest))`, com `--bg-deepest`
 * = `#0a0a0a`). Literal, e NÃO `var(--bg-deepest)`, de propósito: isto é
 * inline style em JS, e no `.ipk` legacy o postcss remove TODAS as defs de
 * custom property do bundle — `var(--bg-deepest)` num style inline vira
 * declaração inválida em runtime no Chr38/53 (ver
 * postcss/legacy-flatten-plugin.mjs). Literal sempre pinta.
 */
const NEUTRAL_PLACEHOLDER_GRADIENT = 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)'

/** Hash estável simples — gera índice na paleta a partir do texto.
 *  Mesmas iniciais sempre dão a mesma cor (consistência entre re-renders
 *  e entre cards do mesmo conteúdo).
 *
 *  Hash usa FNV-1a 32-bit em vez do `* 31` simples — distribuição muito
 *  melhor pra strings curtas (importante: "Anna Moskal" e "Eryk Wróbel"
 *  estavam caindo no mesmo bucket com a função antiga).
 *
 *  ===== GUARD: TEXTO VAZIO NÃO SORTEIA PALETA =====
 *  Sem este guard, `hashToGradient('')` não itera o laço, `hash` fica no
 *  offset basis do FNV, `Math.abs(hash) % 12` dá 1 e a string vazia cai
 *  SEMPRE em `palettes[1]` (`#6b21a8` -> roxo). Como esse roxo é vizinho da
 *  cor de marca (`#7C3AED`), o bloco parecia intencional — foi o "bloco roxo
 *  com ?" que o dono viu na TV real (2.29.0, webOS/Chr53) ao entrar em TV ao
 *  Vivo. Sem texto não há o que colorir: superfície neutra. */
export function hashToGradient(text: string | null | undefined): string {
  if (!text || text.trim().length === 0) return NEUTRAL_PLACEHOLDER_GRADIENT
  let hash = 0x811c9dc5 // FNV offset basis
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) // FNV prime
  }
  // 12 paletas escuras — cobertura ampla pra cast com dezenas de nomes
  // consecutivos sem repetir cor próxima. Cada par é gradient diagonal.
  const palettes: Array<[string, string]> = [
    ['#1e3a8a', '#0f172a'], // azul profundo
    ['#6b21a8', '#1a0a2e'], // roxo
    ['#065f46', '#0c1f1a'], // verde escuro
    ['#991b1b', '#1f0d0d'], // vinho
    ['#92400e', '#1a0f00'], // âmbar profundo
    ['#1e293b', '#0a0a1a'], // grafite azulado
    ['#831843', '#1f0a1a'], // bordô
    ['#0e7490', '#082f3a'], // ciano profundo
    ['#7c2d12', '#1c0a06'], // marrom queimado
    ['#3730a3', '#1e1b4b'], // índigo
    ['#365314', '#1a2e0a'], // verde-oliva escuro
    ['#9d174d', '#2a0a1a'], // rosa escuro
  ]
  const idx = Math.abs(hash) % palettes.length
  const [from, to] = palettes[idx]!
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`
}

/** Qualificadores de qualidade que o catálogo Xtream carimba no nome do canal
 *  ("HD Globo", "Globo SP FHD"). Não são identidade — não viram inicial. */
const QUALITY_QUALIFIERS = /\b(HD|FHD|4K|SD|UHD|H265|H264|HEVC|AVC)\b/gi

/** Ruído de catálogo sujo que não vira inicial: `|BR|`, `[24h]`, `- `, `•`…
 *  Vira espaço (separador), nunca é colado na letra seguinte.
 *
 *  Range Latin-1 explícito DE PROPÓSITO: `\p{L}` exige a flag `u` (ES2018) e
 *  o piso da TV é Chromium 38 — uma regex com `\p{…}`/`u` é SyntaxError no
 *  parse do módulo, ou seja, tela branca, não degradação. */
const NON_WORDISH = /[^0-9A-Za-zÀ-ÖØ-öø-ÿ]+/g

/**
 * ===== A LARGURA DE CADA GLIFO, MEDIDA NA FONTE DA MARCA =====
 * Milésimos de `em` na Plus Jakarta Sans (o valor é o MAIOR entre o avanço e a
 * TINTA do próprio glifo, sobre os pesos 700/800 e os 6 degraus que o app usa).
 * Derivada pela sonda, nunca chutada — ver `scripts/compat/poster-placeholder-probe.mjs`,
 * que reprova se a métrica real da fonte descolar desta tabela.
 *
 * POR QUE A TINTA E NÃO SÓ O AVANÇO: em `Î`/`Ï` o acento pinta muito além da
 * caixa de avanço do `I` (0,287em de avanço contra 0,500em de tinta). Somando
 * só avanço, a conta subestimava 0,16em e uma string emitível estourava o
 * orçamento — achado por varredura de 19 mil strings, não por leitura.
 *
 * Agrupada por largura pra ser AUDITÁVEL: dá pra conferir "o W é o mais largo"
 * de relance. Caractere fora da tabela (`ẞ`, `Œ`, alfabeto não-latino que o
 * `NON_WORDISH` deixe passar) cai no fallback mais LARGO — a degradação é
 * "mostrou 2 iniciais em vez da palavra", nunca "recortou o rótulo".
 */
const GLYPH_WIDTHS: Record<string, number> = {}
const WIDEST_GLYPH = 1111
;(function buildGlyphTable() {
  const grupos: Array<[number, string]> = [
    [1111, 'W'],
    [912, 'M'],
    [889, 'OQÒÓÔÕÖØ'],
    [833, 'AGÀÁÂÃÄÅ'],
    [778, 'CVXYÇÝ'],
    [742, 'NÑ'],
    [740, 'D'],
    [733, 'H'],
    [724, 'UÙÚÛÜ'],
    [722, 'BKR'],
    [667, 'PSZ'],
    [636, 'T'],
    [611, 'EFÈÉÊË'],
    [556, 'L'],
    [500, 'ÎÏ'],
    [396, 'J'],
    [389, 'Í'],
    [333, 'Ì'],
    [287, 'I'],
    // Dígitos (catálogo tem "24H", "E12", "4FUN").
    [722, '0'],
    [413, '1'],
    [611, '269'],
    [612, '3'],
    [682, '4'],
    [615, '5'],
    [591, '7'],
    [636, '8'],
  ]
  for (let g = 0; g < grupos.length; g++) {
    const largura = grupos[g]![0]
    const chars = grupos[g]![1]
    for (let i = 0; i < chars.length; i++) GLYPH_WIDTHS[chars.charAt(i)] = largura
  }
})()

/** Letter-spacing do `.fallback`, em milésimos de `em`. 0.04em em quase toda
 *  caixa, 0.05em no tile do Replay — a conta usa o PIOR. */
const LETTER_SPACING_M = 50
/** Folga pra o que a soma de glifos não captura (kerning adverso, side bearing
 *  negativo nas pontas). O pior excesso medido na varredura foi 0,0009em; 0,06em
 *  é ~66x isso. Barato: o orçamento sobra. */
const INK_SAFETY_M = 60
/**
 * Orçamento de largura do rótulo, em milésimos de `em`. É o MESMO K = 3.31 que a
 * fórmula de degrau de `Poster.module.css` usa pra dimensionar cada caixa — e é
 * por isso que a regra abaixo custa ZERO: qualquer rótulo que passe aqui cabe
 * nas caixas que já existem hoje, sem descer degrau nenhum.
 */
export const LABEL_WIDTH_BUDGET_M = 3310

/**
 * A largura que o rótulo VAI ocupar, em milésimos de `em` — limite SUPERIOR da
 * tinta real (provado por varredura de 19 mil strings contra a métrica do
 * browser; ver o bloco C da sonda). Função pura: sem DOM, sem canvas, sem
 * medir em runtime. Roda no Chr38 igual roda no web.
 *
 * Medir de verdade no runtime seria mais preciso e é EXATAMENTE o que não dá pra
 * fazer aqui: `measureText().actualBoundingBox*` só existe do Chr77 pra cima (o
 * piso da TV é 38), a fonte pode não ter carregado no instante da medida, e
 * seriam centenas de medições por tela de catálogo.
 */
export function estimateLabelWidth(text: string): number {
  let total = INK_SAFETY_M
  for (let i = 0; i < text.length; i++) {
    const w = GLYPH_WIDTHS[text.charAt(i)]
    total += (w === undefined ? WIDEST_GLYPH : w) + LETTER_SPACING_M
  }
  return total
}

/** Comprimento máximo de palavra que pode sair inteira. O orçamento de largura
 *  é quem manda de fato; este teto existe pra manter a regra no espírito de
 *  "sigla/nome curto" e pra limitar o espaço de entradas que a sonda varre. */
const MAX_WHOLE_WORD = 5
/** Até aqui a palavra sai inteira SEM consultar o orçamento: o CSS já está
 *  dimensionado pro pior trigrama possível ("WWW", 3,2751em de tinta MEDIDA,
 *  dentro do K = 3.31). Passar o trigrama pelo orçamento ESTIMADO seria
 *  regressão gratuita — a estimativa é conservadora e reprovaria "WOW"
 *  (3,321 estimado contra 3,081 de tinta real), que é rótulo de catálogo REAL. */
const ALWAYS_WHOLE_LEN = 3

/**
 * Extrai as letras "boas" pra placeholder — nome curto de UMA palavra sai
 * INTEIRO se couber no orçamento de largura; o resto vira 2 iniciais.
 *  - "HBO" → "HBO", "TNT" → "TNT", "MAX" → "MAX", "24H" → "24H"
 *  - "ESPN" → "ESPN", "HGTV" → "HGTV", "BAND" → "BAND" (4 letras que CABEM)
 *  - "Globo" → "GL" e "AXNW" → "AX" (não cabem — ver a conta abaixo)
 *  - "Animal Planet" → "AP" (1ª de cada palavra, max 2)
 *  - "globo sp hd" → "GS" (ignora qualifiers tipo HD/4K)
 *  - "HD Globo" → "GL" (o qualifier some, sobra o nome)
 *
 * ===== O CRITÉRIO É LARGURA, NÃO CONTAGEM DE LETRA (decisão de 11/08) =====
 * O pedido do dono foi "sigla INTEIRA: ESPN vira ESPN, não ES" — reconhecer o
 * canal a 3 metros. O caminho óbvio (soltar o teto de 3 pra 4 ou 5 caracteres)
 * foi MEDIDO antes de ser escrito, e o preço é alto: dimensionar toda caixa pelo
 * pior caso de 4 glifos ("WWWW", 4,36em de tinta) leva o divisor de degrau de
 * 4.31 pra 5.71, e aí 8 caixas descem de degrau e QUATRO ficam sem rótulo
 * nenhum — entre elas a linha da lista de TV ao Vivo, o logo da grade do EPG e o
 * chip do Replay. Ou seja: o "ESPN" apareceria inteiro no pôster grande do VOD
 * exatamente às custas de APAGAR o nome do canal nas telas em que o usuário
 * está procurando canal. Com 5 caracteres são 9 caixas e 6 sem rótulo.
 *
 * O que fura esse dilema é notar que "ESPN" não é largo — ele só é LONGO. A
 * tinta de "ESPN" mede 2,76em, bem abaixo dos 3,31em do pior TRIGRAMA ("WWW")
 * que as caixas de hoje já comportam. Então o teto não precisa ser contagem de
 * caractere: é LARGURA, e o orçamento já está pago. `estimateLabelWidth` decide
 * por caso, e o custo da regra é ZERO — nenhuma caixa muda de degrau, nenhuma
 * perde rótulo, `K` continua 3.31.
 *
 * O que ela NÃO entrega, de propósito, e o preço de entregar (medido):
 *   - "GLOBO" (3,99em) → "GL". Caberia com K = 4.0: +2 caixas com downgrade e
 *     +3 caixas SEM rótulo (tile do Replay, logo do EPG, linha da Live).
 *   - "AXNW" (3,42em) → "AX". Caberia com K = 3.73: +2 caixas sem rótulo.
 * Os dois são decisão de produto, não de engenharia — e a conta está aqui pra
 * ser reaberta com número, não com sensação.
 *
 * ===== POR QUE "TUDO MAIÚSCULA" NÃO É (MAIS) CRITÉRIO =====
 * A versão anterior só devolvia a sigla inteira se `cleaned === cleaned.toUpperCase()`.
 * Isso é premissa quebrada nesta casa: catálogo Xtream vem frequentemente TODO em
 * caixa alta ("GLOBO HD", "TELECINE PREMIUM"), então all-caps não distingue sigla
 * de palavra — só fazia "Max" virar "MA" enquanto "MAX" virava "MAX", que é
 * inconsistência visível pro usuário com dado sujo do MESMO provedor.
 *
 * ===== POR QUE '' E NÃO '?' =====
 * Esta função devolvia `'?'` quando não sobrava nada. Combinado com
 * `hashToGradient('')` (que caía sempre no roxo, ver acima), o resultado era
 * um "?" de 64px sobre um bloco roxo cor-de-marca — o bug que o dono viu na
 * TV. Um "?" não informa NADA que o card já não informe (não há capa): é só
 * ruído grande no meio da tela. Placeholder sem rótulo é superfície limpa, e
 * quem chama não monta o `<span>`. Se um dia quisermos sinal visual aqui, a
 * regra da casa é ícone CSS por token — nunca caractere solto (emoji/glifo
 * pós-2017 vira TOFU no piso Chr38/53).
 *
 * A entrada é defensiva (`null`/`undefined` chegam de dado de provider).
 */
export function extractInitials(text: string | null | undefined): string {
  const cleaned = (text ?? '')
    .replace(QUALITY_QUALIFIERS, ' ')
    .replace(NON_WORDISH, ' ')
    .trim()
  if (cleaned.length === 0) return ''
  const words = cleaned.split(/\s+/).filter(Boolean)
  if (words.length === 1) {
    const whole = words[0]!.toUpperCase()
    // Trigrama e menor: sai inteiro sem consultar o orçamento — o CSS já foi
    // dimensionado pro pior deles ("WWW").
    if (whole.length <= ALWAYS_WHOLE_LEN) return whole
    // 4-5 caracteres: sai inteiro SE couber na mesma largura que as caixas de
    // hoje já reservam. "ESPN"/"HGTV"/"BAND" cabem; "GLOBO"/"AXNW" não.
    if (whole.length <= MAX_WHOLE_WORD && estimateLabelWidth(whole) <= LABEL_WIDTH_BUDGET_M) {
      return whole
    }
    // Não coube: 2 primeiras letras.
    return whole.slice(0, 2)
  }
  // 2+ palavras: primeira letra das 2 primeiras
  return (words[0]![0]! + words[1]![0]!).toUpperCase()
}

/**
 * Adiciona um parâmetro de cache-bust à URL pra forçar um fetch FRESCO
 * (bypassa o SW/browser cache daquela imagem específica).
 *
 * Usado pelo retry de auto-cura: quando um `<img>` falha, pode ser um blip
 * transitório (502 do proxy, entry de cache envenenada por um deploy). Em vez
 * de cair direto no placeholder de letra, tentamos UMA vez com `?cb=<n>` —
 * a URL diferente não casa com a entry ruim no cache, então o SW vai buscar
 * de novo do proxy (que agora responde 200). Se ainda assim falhar, aí sim
 * placeholder.
 *
 * Idempotente o suficiente: se já houver query, concatena com `&`. Não tenta
 * remover um `cb` anterior porque só chamamos isto UMA vez por imagem.
 */
export function appendCacheBust(url: string, token: number | string = 1): string {
  if (!url) return url
  // data:/blob: não têm cache de rede — cache-bust não faz sentido (e quebraria
  // a URL). Retorna intacto.
  if (url.startsWith('data:') || url.startsWith('blob:')) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}cb=${token}`
}

/** Estado da máquina de auto-cura num erro de carga. */
export interface PosterErrorState {
  stage: 0 | 1 | 2
  /** Token de retry: 0 = sem retry; >0 = tentando o cache-bust deste src. */
  retryToken: number
  /** Qual src já teve seu retry consumido (pra não repetir → anti-loop). */
  retriedSrc: string | null
}

/**
 * Reducer puro do `onError` do <Poster>. Decide o PRÓXIMO estado quando a
 * imagem atual (`activeSrc`) falha:
 *
 *   1. Se ainda NÃO retryamos este `activeSrc` → marca como retryado e abre
 *      um retry (retryToken > 0). O componente re-renderiza com cache-bust.
 *   2. Se JÁ retryamos (o próprio retry falhou, ou não há src) → avança o
 *      estágio (src → fallbackSrc → placeholder) e zera o retry.
 *
 * Garante 1 retry por imagem (anti-loop): o `retriedSrc` trava a 2ª tentativa.
 * `now` é injetável pra teste determinístico (default Date.now()).
 */
export function nextPosterErrorState(
  state: PosterErrorState,
  activeSrc: string | null,
  now: number = Date.now(),
): PosterErrorState {
  if (activeSrc && state.retriedSrc !== activeSrc) {
    return { stage: state.stage, retryToken: now, retriedSrc: activeSrc }
  }
  const nextStage: 0 | 1 | 2 = state.stage < 2 ? ((state.stage + 1) as 0 | 1 | 2) : state.stage
  return { stage: nextStage, retryToken: 0, retriedSrc: state.retriedSrc }
}

// Detecta URL conhecidamente quebrada antes de tentar fetch — evita 404
// poluindo console.
function isBrokenUrl(src: string): boolean {
  // placehold.co com segmento de cor vazio entre barras (seed antigo)
  if (/placehold\.co\/[^/]+\/\/[^?]/.test(src)) return true
  // Extensões corrompidas no catálogo de alguns parceiros (ex: .pngo, .jpego)
  if (/\.(pngo|jpgo|jpego|gifo|webpo)(\?|$)/i.test(src)) return true
  return false
}

export function Poster({
  src,
  alt,
  fallbackText,
  fallbackSrc,
  kind = 'poster',
  fallbackKind,
  className,
  placeholderClassName,
  loading = 'lazy',
  fetchPriority,
}: PosterProps) {
  // Cascata de 2 níveis: tenta `src` → fallback `fallbackSrc` → placeholder
  // de iniciais. Cada erro de carga avança 1 estágio. Sem `fallbackSrc`,
  // estágio 1 vai direto pro placeholder.
  const [stage, setStage] = useState<0 | 1 | 2>(0)

  // `|| null`: `rewriteImgUrl` devolve '' quando não há URL carregável — inclui
  // a capa BLOQUEADA pelo guard do caminho direto (URL apontando pra LAN do
  // usuário, ver lib/image-source.ts). String vazia num `<img src>` re-requisita
  // a PÁGINA; aqui ela precisa virar null pra cascata cair no placeholder de
  // iniciais, que é a degradação certa: o usuário vê um card normal sem capa,
  // nunca um erro na cara dele por uma URL que o provedor cadastrou errado.
  const primary = useMemo(() => {
    if (!src || isBrokenUrl(src)) return null
    return rewriteImgUrl(src, { kind }) || null
  }, [src, kind])
  // Fallback usa `fallbackKind` se fornecido, senão herda do primary.
  // Caso típico que precisa override: thumb episódio (kind='thumb', w300)
  // cai pro backdrop da série (precisa w780+ pra não pixelar em 16:9 grande).
  const secondary = useMemo(() => {
    if (!fallbackSrc || isBrokenUrl(fallbackSrc)) return null
    return rewriteImgUrl(fallbackSrc, { kind: fallbackKind ?? kind }) || null
  }, [fallbackSrc, kind, fallbackKind])

  // Determina o src ativo no estágio atual. Pula estágios inválidos
  // (sem URL) automaticamente — placeholder só aparece quando esgotamos.
  const activeSrc = useMemo<string | null>(() => {
    if (stage === 0 && primary) return primary
    if (stage <= 1 && secondary) return secondary
    return null
  }, [stage, primary, secondary])

  // Auto-cura: antes de cair no placeholder, fazemos UM retry com cache-bust
  // pra absorver falhas transitórias (blip de proxy / entry envenenada). Um
  // contador (>0 = estamos no retry da imagem atual). Guardamos qual src já
  // foi retryado pra NÃO entrar em loop (1 retry por imagem, só).
  const [retryToken, setRetryToken] = useState(0)
  const retriedSrcRef = useRef<string | null>(null)

  // Reset quando o conjunto de URLs muda (navegação entre episódios).
  useEffect(() => {
    setStage(0)
    setRetryToken(0)
    retriedSrcRef.current = null
  }, [primary, secondary])

  // URL efetiva no <img>: o src do estágio atual, com cache-bust SE estamos
  // num retry. O cache-bust força o SW/browser a buscar de novo (a URL difere
  // da entry ruim no cache).
  const renderedSrc = useMemo<string | null>(() => {
    if (!activeSrc) return null
    return retryToken > 0 ? appendCacheBust(activeSrc, retryToken) : activeSrc
  }, [activeSrc, retryToken])

  // onError: delega ao reducer puro `nextPosterErrorState` (testado em
  // unidade). 1º erro de um src → retry com cache-bust; 2º erro (ou erro do
  // próprio retry) → avança estágio (→ fallbackSrc → placeholder). O
  // `retriedSrcRef` é o anti-loop (1 retry por imagem).
  const handleError = (): void => {
    const prev: PosterErrorState = {
      stage,
      retryToken,
      retriedSrc: retriedSrcRef.current,
    }
    const next = nextPosterErrorState(prev, activeSrc)
    retriedSrcRef.current = next.retriedSrc
    if (next.stage !== stage) setStage(next.stage)
    setRetryToken(next.retryToken)
  }

  // fetchPriority via setAttribute em ref — React 18.3 emite warning
  // ("does not recognize the `fetchPriority` prop") porque o mapeamento
  // camelCase→lowercase pra essa prop não chegou na 18.3.x. Spread
  // lowercase também emite "Unknown DOM property". setAttribute bypassa
  // o type-check do React e seta o atributo HTML diretamente.
  // Tizen/webOS antigos ignoram atributo desconhecido (noop). Suportado
  // em Chromium 102+ (Tizen 6+ / webOS 6+).
  const imgRef = useRef<HTMLImageElement | null>(null)
  useEffect(() => {
    if (imgRef.current && fetchPriority) {
      imgRef.current.setAttribute('fetchpriority', fetchPriority)
    }
  }, [fetchPriority, renderedSrc])

  // ===== Lazy REAL (polyfill do loading="lazy" via IntersectionObserver) =====
  // Só entra em ação onde o lazy nativo NÃO existe (Chr53/38 das TVs). Onde
  // existe, `inView` já nasce true e o browser decide sozinho.
  const needsLazyPolyfill = loading === 'lazy' && !HAS_NATIVE_LAZY
  const [inView, setInView] = useState(!needsLazyPolyfill)

  useEffect(() => {
    if (!needsLazyPolyfill || inView) return
    const el = imgRef.current
    if (!el) return
    // Sem IO (ambiente muito antigo / jsdom): degrada pro comportamento
    // anterior — carrega tudo. Pior em perf, mas nunca "imagem que não vem".
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    // root: null (viewport) resolve OS DOIS eixos de uma vez. Pela spec, o
    // retângulo de interseção é clipado pelos ancestrais com overflow — então
    // um card rolado pra fora do trilho HORIZONTAL (overflow-x: auto) já conta
    // como não-visível, sem precisar apontar o root pro container do carrossel.
    const io = new IntersectionObserver(
      (entries) => {
        // isEntryVisible, e NÃO `e.isIntersecting`: esse campo só chegou no
        // Chr58. No Chr53 da TV ele é `undefined` → o teste dá sempre falso e
        // NENHUMA imagem carregaria, nem as visíveis. Foi exatamente o que o
        // bench no Chromium 53 real pegou. Ver lib/intersection.ts.
        if (entries.some(isEntryVisible)) {
          setInView(true)
          io.disconnect()
        }
      },
      { rootMargin: LAZY_ROOT_MARGIN },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [needsLazyPolyfill, inView])

  // Fade-in onload (Onda 2 polish). Imagem começa opacity 0 (CSS) e vira
  // 1 quando `loaded=true`. Reset a cada activeSrc mudar (cascata de
  // estágio → próxima URL recomeça com fade). Em vlow/low transition-base
  // é 0ms → comportamento idêntico ao de antes (instant). Em mid+ ganha
  // fade ~150ms.
  //
  // Cache-hit handling: imagem JÁ em cache do browser tem `.complete=true`
  // antes do React anexar `onLoad` — em vários engines (Chromium incluso)
  // `onLoad` NÃO dispara nesse caso. Sem o sync check abaixo, todo poster
  // cacheado ficaria opacity:0 permanente (regressão visual crítica em
  // Home/Movies/Series onde maioria já está em cache). naturalWidth>0
  // distingue cache-hit de erro 404 (que também marca complete=true).
  //
  // `inView` PRECISA estar nas deps: com o lazy polyfill, o `src` só é
  // anexado quando a imagem entra em viewport — e `renderedSrc` já estava
  // calculado desde antes, então ele NÃO muda nesse instante. Sem `inView`
  // aqui, o efeito não re-rodaria no momento em que o <img> ganha src, e uma
  // imagem vinda do cache (complete=true antes do React anexar onLoad, que
  // então não dispara) ficaria presa em opacity:0 pra sempre.
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    setLoaded(false)
    const el = imgRef.current
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [renderedSrc, inView])

  if (!activeSrc) {
    // Esgotou ambos — placeholder de iniciais.
    //
    // A cor é ancorada nas INICIAIS (o invariante da consequência), não no
    // texto cru (a proxy): se não há rótulo pra pintar, não há por que sortear
    // paleta — a superfície é neutra. Ancorar no texto cru deixaria passar
    // qualquer entrada que VIRA vazia depois da limpeza (o caso real:
    // "HD " → iniciais '' mas texto não-vazio → sorteava o roxo de novo).
    const initials = extractInitials(fallbackText)
    // ATENÇÃO ao ler isto: `className` (a classe do <img>) cai no MESMO elemento
    // que `placeholderClassName`. Não existe "só a geometria" — quem quiser
    // desfazer uma propriedade de <img> aqui (`filter`, por exemplo) precisa de
    // (0,2,0), porque as duas classes empatam em (0,1,0) e o desempate seria a
    // ordem das folhas. É o que `.fallback.fallback` faz pelo layout.
    const cls = `${containerStyles.fallback} ${className ?? ''} ${placeholderClassName ?? ''}`
    return (
      <div
        className={cls}
        // Aqui havia `containerType: 'inline-size'`. Ele existia pra alimentar o
        // `clamp(20px, 22cqw, 64px)` do `.initials`, que já saiu — as iniciais
        // agora HERDAM o degrau da caixa. Sem consumidor, ele só aplicava
        // `contain: layout style inline-size` de graça nos engines modernos
        // (web/mobile). Removido com PROVA, não por dedução: a sonda mediu as 18
        // caixas x 4 rótulos x 7 mundos com e sem ele — 504 medições, ZERO
        // diferença de geometria (scripts/compat/poster-placeholder-probe.mjs).
        style={{
          background: initials ? hashToGradient(fallbackText) : NEUTRAL_PLACEHOLDER_GRADIENT,
        }}
        aria-label={alt}
        role="img"
      >
        {/* Sem iniciais, sem `<span>`: placeholder vazio é superfície limpa.
            Montar o span com string vazia deixaria uma caixa de 64px de
            line-height no meio do card sem nada dentro. */}
        {initials ? <span className={containerStyles.initials}>{initials}</span> : null}
      </div>
    )
  }

  return (
    <img
      ref={imgRef}
      // `src` só entra quando a imagem está (ou está prestes a ficar) visível.
      // Sem `src`, o browser NÃO tenta carregar nada: zero download, zero
      // decode, zero memória — e como o CSS já dá dimensão FIXA ao <img>
      // (os tokens `--card-vod-w`/`--card-vod-poster-h`), não há layout shift
      // ao trocar. Spread condicional por causa de `exactOptionalPropertyTypes`.
      {...(inView ? { src: renderedSrc ?? activeSrc } : {})}
      alt={alt}
      className={`${containerStyles.img} ${className ?? ''}`}
      data-loaded={loaded || undefined}
      data-lazy-pending={!inView || undefined}
      // Mantido de propósito: no web/TV moderna o lazy NATIVO é quem trabalha
      // (e aí o polyfill acima nem liga). Nas TVs antigas isto é ignorado em
      // silêncio — o IntersectionObserver é que segura a imagem.
      loading={loading}
      decoding="async"
      // fetchpriority é setado via useEffect acima (não via JSX prop).
      onLoad={() => setLoaded(true)}
      onError={handleError}
    />
  )
}
